import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Logo from '../../components/brand/Logo.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'

/*
 * Help center — GET /help/articles (+ /help/articles/:id, both public).
 * Each article has its own URL (/app/help/:id in-app, /help/:id public) so it can
 * be bookmarked and shared with a client; a "copy link" button gives the public URL.
 * `variant="public"` renders a standalone chrome (no app shell) for logged-out visitors.
 */
export default function HelpCenterPage({ variant = 'app' }) {
  const { t, i18n } = useTranslation()
  const { articleId } = useParams()
  const navigate = useNavigate()
  const { toast } = useFeedback()
  const isPublic = variant === 'public'
  const base = isPublic ? '/help' : '/app/help'

  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(null) // { id, title, content, summary, ... }
  const [openLoading, setOpenLoading] = useState(false)

  useEffect(() => {
    api.get('/help/articles', { params: { lang: i18n.resolvedLanguage } })
      .then(({ data }) => setArticles(data?.articles || []))
      .finally(() => setLoading(false))
  }, [i18n.resolvedLanguage])

  // The URL drives which article is open (deep-linkable / shareable).
  useEffect(() => {
    if (!articleId) { setOpen(null); return }
    let active = true
    setOpenLoading(true)
    api.get(`/help/articles/${articleId}`)
      .then(({ data }) => {
        if (!active) return
        const art = data?.article ?? data
        setOpen({ id: articleId, title: art?.title || '', content: art?.content ?? '' })
      })
      .catch(() => active && setOpen({ id: articleId, title: '', content: '', notFound: true }))
      .finally(() => active && setOpenLoading(false))
  }, [articleId])

  const copyLink = async () => {
    // Always the PUBLIC link, so it can be sent to a client without an account.
    const url = `${window.location.origin}/help/${open.id}`
    try { await navigator.clipboard.writeText(url); toast(t('help.linkCopied'), 'success') }
    catch { toast(url, 'info') }
  }

  const s = q.trim().toLowerCase()
  const filtered = articles.filter((a) => !s || `${a.title} ${a.summary} ${(a.tags || []).join(' ')}`.toLowerCase().includes(s))
  const byCat = {}
  for (const a of filtered) (byCat[a.category || 'other'] ??= []).push(a)

  // ---- article view ----
  const articleView = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Button variant="secondary" icon="chevron-left" onClick={() => navigate(base)}>{t('help.back')}</Button>
        {open && !open.notFound && <Button variant="secondary" icon="link" onClick={copyLink}>{t('help.copyLink')}</Button>}
      </div>
      <Card style={{ padding: '34px 40px' }}>
        {openLoading || !open ? <div style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
          : open.notFound ? <div style={{ color: 'var(--color-text-muted)' }}>{t('help.empty')}</div>
          : (
            <>
              <h1 style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 24, color: 'var(--color-ink)', marginBottom: 18 }}>{open.title}</h1>
              {/* Help articles are trusted, admin-authored HTML (rendered server-side). */}
              <div className="prose" dangerouslySetInnerHTML={{ __html: open.content }} />
            </>
          )}
      </Card>
    </>
  )

  // ---- list view ----
  const listView = (
    <>
      <Card style={{ marginBottom: 18 }}>
        <div style={{ position: 'relative', maxWidth: 420 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={17} /></span>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('help.search')} style={{ height: 42, paddingLeft: 38 }} />
        </div>
      </Card>

      {loading ? <Card>{t('common.loading')}</Card>
        : filtered.length === 0 ? <Card style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 36 }}>{t('help.empty')}</Card>
        : Object.entries(byCat).map(([cat, list]) => (
          <div key={cat} style={{ marginBottom: 24 }}>
            <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12 }}>{t(`help.categories.${cat}`, cat)}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {list.map((a) => (
                <button key={a.id} onClick={() => navigate(`${base}/${a.id}`)} style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--color-border)', borderRadius: 16, boxShadow: 'var(--shadow-card)', padding: 18, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ color: 'var(--color-primary)' }}><Icon name="help" size={18} /></span>
                    <span style={{ fontWeight: 700, color: 'var(--color-ink)', fontSize: 15 }}>{a.title}</span>
                  </div>
                  <p style={{ fontSize: 13.5, color: 'var(--color-text)', lineHeight: 1.5 }}>{a.summary}</p>
                  {(a.tags || []).length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>{a.tags.slice(0, 3).map((tg) => <Badge key={tg} tone="neutral">{tg}</Badge>)}</div>}
                </button>
              ))}
            </div>
          </div>
        ))}
    </>
  )

  const body = articleId ? articleView : listView

  // Public visitors get a standalone chrome (no app sidebar/topbar).
  if (isPublic) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg-page)' }}>
        <header style={{ height: 64, background: '#fff', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
          <a href="/" style={{ display: 'inline-flex' }}><Logo size={24} /></a>
          <LanguageSwitch />
        </header>
        <div style={{ maxWidth: 'var(--content-max, 1180px)', margin: '0 auto', padding: '28px 24px 56px' }}>
          <h1 style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 22, color: 'var(--color-ink)', marginBottom: 18 }}>{t('help.title')}</h1>
          {body}
        </div>
      </div>
    )
  }

  return <Page title={t('help.title')}>{body}</Page>
}
